/**
 * O total do que está filtrado, no cabeçalho de Transações.
 *
 * ── Por que não é UM número ───────────────────────────────────────────────
 *
 * A lista mistura três naturezas: o que entrou, o que saiu, e o que foi
 * transferido para reserva. Somar as três dá um valor que não significa nada
 * — recebeu 3 mil e gastou 3 mil viraria R$ 0,00, um número que parece dizer
 * "nada aconteceu" sobre um mês inteiro de movimento.
 *
 * Então a regra é: **um número quando o recorte já isola uma natureza, e a
 * recusa de somar quando ele mistura.** A recusa é a informação.
 *
 * ── Transferência nunca conta como gasto ──────────────────────────────────
 *
 * Mandar dinheiro para a reserva sai do caixa mas não é consumo. Método com
 * prefixo `__debit__` já fica fora do cálculo de saldo no backend e só
 * aparece na exibição; juntá-lo aos gastos aqui faria o cabeçalho discordar
 * do resto do app na direção mais cara — parecendo que ela gastou o que na
 * verdade guardou.
 *
 * ── Soma `valorExibido`, e não `t.value` (15/09) ─────────────────────────
 *
 * Para mensal de valor variável, `t.value` é o CADASTRADO e o do mês mora em
 * `displayValue`. A linha mostra o do mês; o total tem que somar o mesmo.
 *
 * ── Parcela: `t.value` já é a parcela ────────────────────────────────────
 *
 * Verificado em `projectionCalc.amountInMonth`: para o mês de uma parcela ele
 * devolve `t.value`, ou seja o valor guardado JÁ é o da parcela e não o da
 * compra inteira. A linha da lista mostra esse mesmo campo, então o total é
 * a soma do que está na tela — não uma conta nova sobre o dinheiro dela.
 *
 * Módulo puro, testável sem renderizar nada.
 */
import { Transaction, TransactionType } from '../types/Transaction';
import { valorExibido } from './monthView';

export const DEBIT_PREFIX = '__debit__';
export const WITHDRAW_PREFIX = '__withdraw__';

/** Reserva/investimento, nas duas direções. Não é receita nem despesa. */
export const ehTransferencia = (t: Transaction): boolean =>
  t.debitDirection
    ? t.debitDirection === 'debit' || t.debitDirection === 'withdraw'
    : !!(t.paymentMethod?.startsWith(DEBIT_PREFIX) || t.paymentMethod?.startsWith(WITHDRAW_PREFIX));

export interface ResumoDoFiltro {
  count: number;
  /** Só consumo. Transferência para reserva NÃO entra aqui. */
  despesa: number;
  receita: number;
  /** Movimentação de reserva, nas duas direções, sempre positiva. */
  transferencia: number;
}

export const resumirFiltro = (transacoes: Transaction[]): ResumoDoFiltro => {
  const r: ResumoDoFiltro = { count: transacoes.length, despesa: 0, receita: 0, transferencia: 0 };

  for (const t of transacoes) {
    // Cada linha cai em UM balde só — se cruzassem, o cabeçalho contaria o
    // mesmo dinheiro duas vezes.
    const v = valorExibido(t);
    if (ehTransferencia(t)) r.transferencia += Math.abs(v);
    else if (t.type === TransactionType.INCOME) r.receita += v;
    else r.despesa += v;
  }
  return r;
};

/**
 * O que o cabeçalho de fato desenha.
 *
 * Devolve as partes já decididas para a UI só imprimir — assim a regra do
 * "um número ou dois" tem um dono só e é testável sem DOM.
 */
export interface ParteDoTotal {
  chave: 'despesa' | 'receita' | 'transferencia';
  valor: number;
  /** Sinal a exibir. `null` quando o recorte já isola a natureza. */
  sinal: '-' | '+' | null;
  titulo: string;
}

/**
 * O cabeçalho fica QUIETO enquanto há uma pergunta respondida.
 *
 * A caixa da resposta já mostra "1 lançamento · R$ 57,64", e aquele número
 * vem do BACKEND — `applyQuery` soma o que PESA no período, não o valor da
 * linha: uma parcela de R$ 100 dentro de um recorte de três meses vale R$ 300
 * lá (`transactionQuery.ts:145`).
 *
 * O total daqui soma o valor de cada linha visível. Os dois estão certos para
 * perguntas diferentes, e por isso não podem dividir a tela: seriam dois
 * números de dinheiro discordando a dez centímetros um do outro, que é
 * exatamente o defeito dos três "saldo" que o app passou três sessões
 * consertando.
 *
 * Uma dona por contexto: **filtro → cabeçalho, pergunta → caixa da resposta.**
 */
export const totaisParaCabecalho = (
  transacoes: Transaction[],
  perguntaAtiva: boolean,
): ParteDoTotal[] => (perguntaAtiva ? [] : partesDoTotal(resumirFiltro(transacoes)));

export const partesDoTotal = (r: ResumoDoFiltro): ParteDoTotal[] => {
  const temDespesa = r.despesa > 0;
  const temReceita = r.receita > 0;
  // Só mostra sinal quando há mais de uma natureza de FLUXO na tela. Com um
  // tipo só, o sinal é ruído: ela filtrou por despesa, sabe que é despesa.
  const mistura = temDespesa && temReceita;

  const partes: ParteDoTotal[] = [];
  if (temDespesa) {
    partes.push({ chave: 'despesa', valor: r.despesa, sinal: mistura ? '-' : null, titulo: 'Total em despesas' });
  }
  if (temReceita) {
    partes.push({ chave: 'receita', valor: r.receita, sinal: mistura ? '+' : null, titulo: 'Total em receitas' });
  }
  if (r.transferencia > 0) {
    // Sempre com rótulo próprio, nunca somada às despesas.
    partes.push({ chave: 'transferencia', valor: r.transferencia, sinal: null, titulo: 'Movimentado em reserva' });
  }
  return partes;
};
